import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isNativeShell } from './platform.js';

/**
 * Voz nativa no Android: enquanto a conversa ao vivo dura, um serviço do
 * aparelho mantém o microfone com a tela bloqueada e mostra um aviso fixo com
 * "Silenciar" e "Desligar" (o botão do fone também silencia). O atalho do ícone
 * e o widget "Falar" pedem a conversa. Fora do app Android, nada aqui faz efeito.
 */

export type LiveAction = 'mute' | 'hangup';

type VoicePlugin = {
  startLive(options: { name: string; muted: boolean }): Promise<void>;
  updateLive(options: { name: string; muted: boolean }): Promise<void>;
  stopLive(): Promise<void>;
  assistantStatus(): Promise<{ held: boolean }>;
  openAssistantSettings(): Promise<void>;
  addListener(
    event: 'liveAction',
    handler: (data: { action: LiveAction }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(event: 'talk', handler: () => void): Promise<PluginListenerHandle>;
};

const plugin = registerPlugin<VoicePlugin>('SecretarioVoice');

export function liveServiceAvailable(): boolean {
  try {
    return (
      isNativeShell() &&
      Capacitor.getPlatform() === 'android' &&
      Capacitor.isPluginAvailable('SecretarioVoice')
    );
  } catch {
    return false;
  }
}

async function quiet(run: () => Promise<void>): Promise<void> {
  if (!liveServiceAvailable()) return;
  try {
    await run();
  } catch {
    /* sem o serviço: a conversa segue com a tela acesa, como antes */
  }
}

/** A conversa começou: o microfone continua com a tela bloqueada. */
export function startLiveService(name: string, muted: boolean): Promise<void> {
  return quiet(() => plugin.startLive({ name, muted }));
}

/** O microfone mudou: o aviso fixo acompanha. */
export function updateLiveService(name: string, muted: boolean): Promise<void> {
  return quiet(() => plugin.updateLive({ name, muted }));
}

export function stopLiveService(): Promise<void> {
  return quiet(() => plugin.stopLive());
}

async function listen(register: () => Promise<PluginListenerHandle>): Promise<() => void> {
  if (!liveServiceAvailable()) return () => {};
  try {
    const handle = await register();
    return () => void handle.remove();
  } catch {
    return () => {};
  }
}

/** "Silenciar" ou "Desligar" no aviso, ou o botão do fone. Devolve como desligar. */
export function onLiveAction(handler: (action: LiveAction) => void): Promise<() => void> {
  return listen(() => plugin.addListener('liveAction', (data) => handler(data.action)));
}

/** Atalho do ícone ou widget "Falar". Devolve como desligar. */
export function onTalkRequest(handler: () => void): Promise<() => void> {
  return listen(() => plugin.addListener('talk', handler));
}

/** O Secretário é o assistente do celular? null fora do app Android. */
export async function assistantHeld(): Promise<boolean | null> {
  if (!liveServiceAvailable()) return null;
  try {
    return (await plugin.assistantStatus()).held;
  } catch {
    return null;
  }
}

/** Abre a tela do aparelho onde se escolhe o assistente digital. */
export async function openAssistantSettings(): Promise<boolean> {
  if (!liveServiceAvailable()) return false;
  try {
    await plugin.openAssistantSettings();
    return true;
  } catch {
    return false;
  }
}
